import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

import { MatSnackBarService } from '@ib-core/services/snackbar.service';
import { UiTouchscreenService } from '@ib-core/services/ui-touchscreen.service';
import { helperBase64ToBytes } from '@ib-shared/helpers/base64-to-bytes';
import { helperCreateId } from '@ib-shared/helpers/create-id';
import { helperFileToBase64 } from '@ib-shared/helpers/file-to-base-64';
import { forkJoin, of } from 'rxjs';

import {
  ISelectedDocument,
  SELECTED_DOC_ID_PREFIX,
} from '../selected-docs/selected-docs.component';

@Component({
  selector: 'ib-upload',
  templateUrl: './upload.component.html',
})
export class UploadComponent implements AfterViewInit {
  @Input() btnClass: string;
  @Input() addedDocs: Array<ISelectedDocument>;
  @Input() disabled: boolean;

  public files: Array<File>;
  public toggle: boolean; // for reset input
  public fileHandling = false;

  @Output() selected = new EventEmitter<Array<ISelectedDocument>>();

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  constructor(
    private snackbarS: MatSnackBarService,
    public uiTsS: UiTouchscreenService,
  ) {}

  ngAfterViewInit(): void {
    this.fileInput.nativeElement.value = '';
  }
  onClickFileInputButton(): void {
    this.fileInput.nativeElement.click();
  }

  isFileAlreadyAdded(file: File): boolean {
    return this.addedDocs?.some(
      i =>
        i.file &&
        i.file.name === file.name &&
        i.file.type === file.type &&
        i.file.size === file.size,
    );
  }

  onChangeFileInput(): void {
    this.fileHandling = true;
    this.toggle = !this.toggle;
    let files = Object.values(
      this.fileInput.nativeElement.files || {},
    ) as Array<File>;
    files = files
      .filter(file => {
        // < 20 mb
        if (file.size > 20971520) {
          this.snackbarS.show({
            msg: `File '${file.name}' is too big`,
            type: 'error',
          });
          return null;
        } else if (this.isFileAlreadyAdded(file)) {
          this.snackbarS.show({
            msg: `File '${file.name}' already added`,
            type: 'error',
          });
          return null;
        } else {
          return file;
        }
      })
      .filter(i => i);
    if (files.length) {
      forkJoin(
        files.map(f => {
          return this.isFileImage(f) ? helperFileToBase64(f) : of(null);
        }),
      ).subscribe(base64List => {
        const docs: Array<ISelectedDocument> = files.map((f, index) => {
          const base64 = base64List[index] as string;
          const r: ISelectedDocument = {
            id: helperCreateId(SELECTED_DOC_ID_PREFIX),
            name: f.name?.split('.').slice(0, -1).join('.'),
            file: f,
            base64: this.isFileImage(f) && base64 ? base64 : undefined,
            isFileImage: this.isFileImage(f),
            isFilePdf: this.isFilePdf(f),
            createdAt: new Date(),
            sizeBytes:
              f?.size || 0 || (base64 ? helperBase64ToBytes(base64) : 0),
          };
          return r;
        });
        this.fileHandling = false;
        this.selected?.emit(docs);
      });
    } else {
      this.fileHandling = false;
    }
  }

  private isFileImage(file: File): boolean {
    const acceptedImageTypes = ['image/gif', 'image/jpeg', 'image/png'];
    return file && acceptedImageTypes.includes(file['type']);
  }

  private isFilePdf(file: File): boolean {
    return file.type === 'application/pdf';
  }
}
